import axios from "@/util/axios";
import {APICredential} from "@/interfaces/entities/APICredential";
import {VuexActionContext} from "@/store/index";
import {WEBHOOK_TYPES, WebhookUrl} from "@/interfaces/entities/WebhookUrl";
import {DataUsageThresholdConfig} from "@/interfaces/entities/DataUsageThresholdConfig";

export interface APIState {
    apiChannels: APICredential[];
    balanceAlertUrl: string;
    dataUsageThresholdUrl: string;
    dataUsageThresholdRates: number[];
    eSimStatusUrl: string;
    customerRegistrationUrl: string;
    countryChangeUrl: string;
    balanceActivationUrl: string;
}

export const apiState: APIState = {
    apiChannels: [],
    balanceAlertUrl: "",
    dataUsageThresholdUrl: "",
    dataUsageThresholdRates: [],
    eSimStatusUrl: "",
    customerRegistrationUrl: "",
    countryChangeUrl: "",
    balanceActivationUrl: ""
};
export const APIMutations = {
    SET_CHANNELS(state: APIState, apiCredentials: APICredential[]) {
        state.apiChannels = apiCredentials || [];
    },
    SET_CHANNEL_NAME(state: APIState, payload: APICredential) {
        if (!state.apiChannels || state.apiChannels.length <= 0) return;
        const channel = state.apiChannels.find((c: APICredential) => c.clientId === payload.clientId);
        if (channel) channel.name = payload.name;

        // TODO: Attach API to update this info

    },
    UPSERT_CHANNEL(state: APIState, apiCredential: APICredential) {
        if (!state.apiChannels) state.apiChannels = [];

        const hasId = apiCredential.id !== undefined && apiCredential.id !== null;
        const index = state.apiChannels.findIndex((c: APICredential) => {
            if (hasId) return c.id === apiCredential.id;
            return c.clientId === apiCredential.clientId;
        });

        if (index >= 0) state.apiChannels.splice(index, 1, apiCredential);
        else state.apiChannels.push(apiCredential);
    },
    REMOVE_CHANNEL(state: APIState, salesChannelId: number) {
        if (!state.apiChannels) return;
        state.apiChannels = state.apiChannels.filter((c: APICredential) => c.id !== salesChannelId);
    },
    ADD_NEW_CHANNEL(state: APIState, apiCredential: APICredential) {
        if (!state.apiChannels) return;
        if(state.apiChannels.find((item: APICredential) => item.clientId === apiCredential.clientId)) return;
        state.apiChannels.push(apiCredential);
    },
    ADD_BALANCEALERT_URL(state: APIState, url: string) {
        state.balanceAlertUrl = url;
    },
    ADD_DATAUSAGETHRESHOLD_URL(state: APIState, url: string) {
        state.dataUsageThresholdUrl = url;
    },
    SET_DATA_USAGE_THRESHOLD_RATES(state: APIState, rates: number[]) {
        state.dataUsageThresholdRates = rates;
    },
    ADD_ESIMSTATUS_URL(state: APIState, url: string) {
        state.eSimStatusUrl = url;
    },
    ADD_CUSTOMERREGISTRATION_URL(state: APIState, url: string) {
        state.customerRegistrationUrl = url;
    },
    ADD_COUNTRYCHANGE_URL(state: APIState, url: string) {
        state.countryChangeUrl = url;
    },
    ADD_BALANCEACTIVATION_URL(state: APIState, url: string) {
        state.balanceActivationUrl = url;
    }
};

export const APIActions = {
    async FETCH_API_CREDENTIALS({commit}: VuexActionContext<APICredential>) {
        await axios.post("/users/current/api-credentials/sync");
        const response = await axios.get("/users/current/api-credentials");
        commit("SET_CHANNELS", response.data);
        console.log("[APIStore] Got API credentials!", response.data);
    },
    async CREATE_API_CREDENTIALS({commit}: VuexActionContext<APICredential>): Promise<APICredential> {
        const response = await axios.post("/users/current/api-credentials");
        const created: APICredential = response.data;
        commit("UPSERT_CHANNEL", created);
        return created;
    },
    async RENEW_API_CREDENTIALS(
        {commit}: VuexActionContext<APICredential>,
        salesChannelId: number
    ): Promise<APICredential> {
        const response = await axios.post(`/users/current/api-credentials/${salesChannelId}/renew`);
        const renewed: APICredential = response.data;
        commit("UPSERT_CHANNEL", renewed);
        return renewed;
    },
    async DELETE_API_CREDENTIALS(
        {commit}: VuexActionContext<number>,
        salesChannelId: number
    ): Promise<void> {
        await axios.delete(`/users/current/api-credentials/${salesChannelId}`);
        commit("REMOVE_CHANNEL", salesChannelId);
    },
    async UPDATE_API_SALES_CHANNEL(
        {commit}: VuexActionContext<APICredential>,
        payload: { name: string; clientId: string }
    ): Promise<APICredential> {
        const response = await axios.put("/users/current/api-credentials", {
            name: payload.name,
            clientId: payload.clientId
        });
        const updated: APICredential = response.data;
        commit("UPSERT_CHANNEL", updated);
        return updated;
    },
    async SAVE_APIWEBHOOK_URL(context: VuexActionContext<void>, data: WebhookUrl) {
        const response =  await axios.post(`/users/current/webhook-url/${data.type}`,{url: data.url});
        console.log("[APIStore] Saved webhook url for device verification!", response.status);
        return response;
    },
    async FETCH_APIWEBHOOK_URL({commit}: VuexActionContext<string>, type: string) {
        console.log("type", WEBHOOK_TYPES[type as keyof typeof WEBHOOK_TYPES]);
        const response =  await axios.get(`/users/current/webhook-url/${WEBHOOK_TYPES[type as keyof typeof WEBHOOK_TYPES]}`);
        console.log(`[APIStore] Fetched webhook url for ${type}!`, response.status);
        const mutation = `ADD_${type.replace(/_/g, "")}_URL`;
        if (response.data.length > 0) {
            commit(mutation, response.data[0].url);
        } else {
            commit(mutation, "");
        }
    },
    async FETCH_DATA_USAGE_THRESHOLDS({commit}: VuexActionContext<void>) {
        const response = await axios.get("/users/current/data-usage-thresholds");
        commit("SET_DATA_USAGE_THRESHOLD_RATES", response.data.rates || []);
    },
    async SAVE_DATA_USAGE_THRESHOLDS(context: VuexActionContext<void>, rates: number[]) {
        const response = await axios.put("/users/current/data-usage-thresholds", {rates} as DataUsageThresholdConfig);
        return response;
    }
};
